import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Mail, ShieldCheck } from "lucide-react";

export default function AuthModal() {
  const { authModalOpen, setAuthModalOpen, login, register, googleLogin } = useAuth();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "seeker" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async () => {
    setBusy(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        await register(form.name, form.email, form.password, form.role);
        toast.success("Account created & verified (mock OTP)");
      }
      setAuthModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authModalOpen} onOpenChange={setAuthModalOpen}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Welcome to Rent-ya-Buy</DialogTitle>
        </DialogHeader>
        <Button
          data-testid="google-login-button"
          variant="outline"
          className="w-full gap-2 border-slate-300"
          onClick={googleLogin}
        >
          <Mail className="h-4 w-4 text-red-500" /> Continue with Google
        </Button>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="h-px flex-1 bg-border" /> or use email <div className="h-px flex-1 bg-border" />
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="auth-tab-login">Login</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">Register</TabsTrigger>
          </TabsList>
          <TabsContent value="login" className="space-y-3 pt-3">
            <div className="space-y-1">
              <Label>Email</Label>
              <Input data-testid="login-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
            </div>
            <div className="space-y-1">
              <Label>Password</Label>
              <Input data-testid="login-password-input" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit-button" className="w-full" disabled={busy} onClick={submit}>
              {busy ? "Signing in..." : "Sign In"}
            </Button>
          </TabsContent>
          <TabsContent value="register" className="space-y-3 pt-3">
            <div className="space-y-1">
              <Label>Full Name</Label>
              <Input data-testid="register-name-input" value={form.name} onChange={set("name")} placeholder="Your name" />
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input data-testid="register-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
            </div>
            <div className="space-y-1">
              <Label>Password</Label>
              <Input data-testid="register-password-input" type="password" value={form.password} onChange={set("password")} placeholder="Min 6 characters" />
            </div>
            <div className="space-y-1">
              <Label>I am a</Label>
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger data-testid="register-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="seeker" data-testid="role-seeker">Property Seeker</SelectItem>
                  <SelectItem value="owner" data-testid="role-owner">Property Owner</SelectItem>
                  <SelectItem value="agent" data-testid="role-agent">Agent / Broker</SelectItem>
                  <SelectItem value="builder" data-testid="role-builder">Builder / Developer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="register-submit-button" className="w-full" disabled={busy} onClick={submit}>
              {busy ? "Creating..." : "Create Account"}
            </Button>
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <ShieldCheck className="h-3 w-3 text-emerald-600" /> Mobile OTP / Gmail verification is simulated (mock) in this build.
            </p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
